@if (loading) {
  <div>
    <h3 class="panel-title"><svg lucideLoaderCircle class="lucide-spin"></svg> Loading...</h3>
  </div>
} @else {
  <!-- The host sits outside the form/launching split so a failure arriving while the review
       view is up is still claimed; `launching` clears a tick later and the form returns. -->
  <div
    class="flex-left-column light-control-host"
    arpSurfaceErrorHost="launch-test-dialog"
    #dialogError="surfaceErrorHost"
  >
    <div class="modal-header">
      @if (!launching) {
        <h3 class="panel-title">Launch test of "{{ !!application ? application.name : '' }}"</h3>
      } @else {
        <h3 class="panel-title launching-headline">
          <svg lucideLoaderCircle class="lucide-spin"></svg>
          Preparing to launch your test...
        </h3>
      }
      <button
        type="button"
        class="modal-close-btn"
        (click)="onCancelClick()"
        [disabled]="launching"
        [pTooltip]="launching ? 'Please wait' : 'Close'"
        tooltipPosition="left"
        aria-label="Close"
      >
        <svg lucideX></svg>
      </button>
    </div>
    @if (!launching) {
      <div
        id="modal-step-app-recovery"
        class="modal-step modal-step--collapsible"
        [class.is-open]="isStepOpen('app-recovery')"
      >
        <div
          class="modal-step-label"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="isStepOpen('app-recovery')"
          aria-controls="modal-step-app-recovery-body"
          (click)="toggleStep('app-recovery')"
          (keydown)="onStepHeaderKeydown($event, 'app-recovery')"
        >
          <span class="modal-step-number"></span>
          <h3 class="modal-step-title">Applications &amp; recovery point</h3>
          @if (totalEligibleApplicationsCount > 0) {
            <div class="modal-step-actions">
              <span class="selection-count">
                <strong>{{ selectedApplications.length }}</strong> of {{ totalEligibleApplicationsCount }} selected
              </span>
              @if (recoveryPointTime) {
                <span class="step-summary-divider">·</span>
              }
              @if (recoveryPointTime) {
                <span class="recovery-point-summary">
                  <strong>{{ recoveryPointTime }}</strong>
                </span>
              }
            </div>
          }
          <svg lucideChevronDown class="modal-step-chevron"></svg>
        </div>
        @if (isStepOpen('app-recovery')) {
          <div id="modal-step-app-recovery-body" class="modal-step-body">
            <div class="modal-step-section">
              <h4>Applications</h4>
              <p class="modal-body-description">
                Select applications to test recovery to {{ !!application ? application.targetRegion : '' }}
              </p>
              @if (totalEligibleApplicationsCount > 5) {
                <div class="app-search-wrap">
                  <svg lucideSearch class="app-search-icon"></svg>
                  <input
                    type="text"
                    class="app-search-input"
                    placeholder="Search applications..."
                    [(ngModel)]="applicationSearchFilter"
                    aria-label="Search applications"
                  />
                  @if (applicationSearchFilter) {
                    <button
                      type="button"
                      class="app-search-clear"
                      (click)="applicationSearchFilter = ''"
                      aria-label="Clear search"
                    >
                      <svg lucideX></svg>
                    </button>
                  }
                </div>
              }
              @if (totalEligibleApplicationsCount === 0) {
                <div class="app-selection-empty">
                  <svg lucideInfo></svg>
                  <span>No other applications are available to test with this one.</span>
                </div>
              }
              @if (totalEligibleApplicationsCount > 0 && applications.length === 0) {
                <div class="app-selection-empty">
                  <svg lucideInfo></svg>
                  <span>No applications match "{{ applicationSearchFilter }}".</span>
                </div>
              }
              @if (applications.length > 0) {
                <div class="app-list-toolbar">
                  <button
                    type="button"
                    class="select-all-link"
                    (click)="toggleSelectAllApplications(!areAllApplicationsSelected())"
                  >
                    {{ areAllApplicationsSelected() ? 'Deselect all' : 'Select all' }}
                  </button>
                </div>
              }
              @if (applications.length > 0) {
                <div class="app-selection-list" id="applicationSelectionList">
                  @for (application of applications; track applicationTrackBy($index, application)) {
                    <ng-container *ngLet="computeApplicationRecoveryPoint(application.applicationObj) as appRp">
                      <label
                        class="app-selection-row"
                        [class.selected]="application.selected"
                        [class.has-error]="
                          application.selected &&
                          !appRp &&
                          !isRefreshingApplicationRecoveryPoints(application.applicationObj)
                        "
                      >
                        <input
                          class="arpio-checkbox"
                          type="checkbox"
                          (input)="toggleApplicationSelection(application.applicationObj.applicationId)"
                          [checked]="applicationIsChecked(application.applicationObj)"
                        />
                        <span class="app-name">{{ application.applicationObj.name }}</span>
                        @if (application.selected) {
                          @if (appRp) {
                            @if (showLock(appRp)) {
                              <svg lucideLock class="lock-icon"></svg>
                            }
                            <span class="recovery-timestamp-text">
                              {{ recoversToTimeString(application.applicationObj, appRp) }}
                            </span>
                          } @else {
                            @if (!isRefreshingApplicationRecoveryPoints(application.applicationObj)) {
                              <span class="recovery-timestamp-error">No matching recovery point</span>
                            }
                          }
                        }
                      </label>
                    </ng-container>
                  }
                </div>
              }
            </div>
            <div class="modal-step-section">
              <h4>Recovery point</h4>
              <p class="modal-body-description">
                Select the point in time to restore. Note: Applications in the list above will display the closest
                available recovery point to the point in time you select in the slider.
              </p>
              @if (showRetainedRpsCheckbox) {
                <label class="retained-rps-toggle">
                  <input
                    id="only-protected-rps"
                    class="arpio-checkbox"
                    type="checkbox"
                    (input)="computeApplicationsRecoveryPoint()"
                    [(ngModel)]="onlyProtectedRps"
                  />
                  <span class="retained-rps-label">
                    Restrict to indefinitely-retained Recovery Points
                    <svg lucideLock class="lock-icon"></svg>
                  </span>
                </label>
              }
              @if (canAdjustRecoveryPoint) {
                <rp-range-slider
                  class="full-width"
                  [recoveryPoints]="recoveryPoints"
                  [enableRealTimeRecovery]="enableRealTimeRecovery"
                  (update)="recoveryPointChanged($event)"
                >
                </rp-range-slider>
              } @else {
                <div class="rp-locked-banner">
                  <svg lucideLock class="lock-icon"></svg>
                  <span
                    >Recovery Point locked at <strong>{{ recoveryPointTime }}</strong></span
                  >
                </div>
              }
              <recovery-time-info
                [realTime]="selectedIsRealTime"
                [snapshotTime]="recoveryPointSnapshotTime"
              ></recovery-time-info>
            </div>
            <div class="modal-step-continue-row">
              <button
                type="button"
                class="arpio-secondary-button"
                data-cy="step-continue-button"
                (click)="continueToStep(nextStepAfterAppRecovery)"
              >
                Continue
              </button>
            </div>
          </div>
        }
      </div>
      @if (accountService.networkSandboxEnabled) {
        <div id="modal-step-sandbox" class="modal-step modal-step--collapsible" [class.is-open]="isStepOpen('sandbox')">
          <div
            class="modal-step-label"
            role="button"
            tabindex="0"
            [attr.aria-expanded]="isStepOpen('sandbox')"
            aria-controls="modal-step-sandbox-body"
            (click)="toggleStep('sandbox')"
            (keydown)="onStepHeaderKeydown($event, 'sandbox')"
          >
            <span class="modal-step-number"></span>
            <h3 class="modal-step-title">Network sandbox</h3>
            <div class="modal-step-actions">
              <span>{{ networkSandboxChecked ? 'On' : 'Off' }}</span>
            </div>
            <svg lucideChevronDown class="modal-step-chevron"></svg>
          </div>
          @if (isStepOpen('sandbox')) {
            <div id="modal-step-sandbox-body" class="modal-step-body">
              <form [formGroup]="launchTestSettingsFormModel">
                <div class="sandbox-toggle" [class.modal-section-disabled]="sandboxSectionDisabled">
                  <!-- tabindex/role let keyboard + screen-reader users reach the
                      tooltip explaining why the checkbox is disabled, since the
                      disabled checkbox itself is skipped by the tab order. -->
                  <span
                    [pTooltip]="sandboxSectionDisabled ? sandboxDisabledReason : null"
                    tooltipPosition="top"
                    [attr.tabindex]="sandboxSectionDisabled ? 0 : null"
                    [attr.role]="sandboxSectionDisabled ? 'note' : null"
                    [attr.aria-label]="sandboxSectionDisabled ? sandboxDisabledReason : null"
                  >
                    <input
                      id="sandbox-checked"
                      class="arpio-checkbox"
                      type="checkbox"
                      formControlName="sandboxChecked"
                    />
                  </span>
                  <div class="sandbox-toggle-content">
                    <label for="sandbox-checked">
                      <h4>Enable Network Sandbox</h4>
                    </label>
                    <!-- `&nbsp;` before "Learn more." — don't strip; keeps the link bonded to the sentence. -->
                    @if (isAzureApplication) {
                      <div class="modal-subtitle">
                        Block outbound internet connectivity from virtual network resources to prevent possible
                        interaction with production services.&nbsp;<a
                          href="{{ uiService.azureNetworkSandboxDocsUrl }}"
                          target="_blank"
                          >Learn more.</a
                        >
                      </div>
                    }
                    @if (!isAzureApplication) {
                      <div class="modal-subtitle">
                        Block outbound internet connectivity from VPC resources to prevent possible interaction with
                        production services.&nbsp;<a href="{{ uiService.networkSandboxDocsUrl }}" target="_blank"
                          >Learn more.</a
                        >
                      </div>
                    }
                  </div>
                </div>
                @if (
                  accountService.networkSandboxWhitelistEnabled && networkSandboxChecked && canSandboxRecoveryPoint
                ) {
                  <div class="sandbox-options" [class.modal-section-disabled]="sandboxFrozen">
                    <!-- Network Firewall requires an IGW in the recovery point. -->
                    @if (showAdvancedSandboxOptions && !isAzureApplication) {
                      <div>
                        <label
                          class="arpio-radio"
                          [class.modal-section-disabled]="!sandboxFrozen && !shouldShowAdvancedSandboxOption"
                        >
                          <input
                            type="radio"
                            id="advanced-sandbox"
                            [checked]="effectiveAdvancedNetworkSandbox"
                            (change)="setAdvancedNetworkSandbox(true)"
                            [attr.disabled]="sandboxFrozen || !shouldShowAdvancedSandboxOption ? '' : null"
                          />
                          <span class="radio-label">Manage outbound access by network firewall service</span>
                        </label>
                        @if (!shouldShowAdvancedSandboxOption) {
                          <div class="sandbox-unavailable-note">
                            <svg lucideInfo></svg>
                            <p>
                              Network firewall is only available for recovery points that contain an internet gateway.
                              <a href="{{ uiService.networkSandboxDocsUrl }}" target="_blank">Learn more.</a>
                            </p>
                          </div>
                        }
                      </div>
                    }
                    @if (effectiveAdvancedNetworkSandbox) {
                      <div class="nested-content">
                        <h5 class="margin-top">Allow outbound access to these domains</h5>
                        <div class="sandbox-scroll-list">
                          <string-list-control
                            class="spacer"
                            [validationFunction]="isValidDomain"
                            [listValidationFunction]="domainListIssues"
                            [placeholderText]="domainAllowlistPlaceholderText"
                            [frozen]="sandboxFrozen"
                            (validated)="sandboxDomainsValidated($event)"
                            formControlName="sandboxAllowedDomains"
                          ></string-list-control>
                        </div>
                        <arp-field-issue [message]="domainIssueMessage"></arp-field-issue>
                        @if (!sandboxFrozen && suggestedDomains.length > 0) {
                          <div>
                            <h5>Suggested Domains</h5>
                            <div class="sandbox-scroll-list">
                              @for (suggestion of suggestedDomains; track suggestion) {
                                <div class="suggestion">
                                  <button class="arpio-button" (click)="addAllowedDomain(suggestion.value)">
                                    <svg lucidePlus></svg>
                                  </button>
                                  {{ suggestion.value }}
                                  <span class="suggestion-reason">({{ suggestion.reason }})</span>
                                </div>
                              }
                            </div>
                          </div>
                        }
                        <ng-container *ngTemplateOutlet="cidrBlocksTemplate"></ng-container>
                      </div>
                    }
                    @if (showAdvancedSandboxOptions && !isAzureApplication) {
                      <div>
                        <label
                          class="arpio-radio"
                          [class.modal-section-disabled]="!sandboxFrozen && !shouldShowBasicSandboxOption"
                        >
                          <input
                            type="radio"
                            id="basic-sandbox"
                            [checked]="!effectiveAdvancedNetworkSandbox"
                            (change)="setAdvancedNetworkSandbox(false)"
                            [attr.disabled]="sandboxFrozen || !shouldShowBasicSandboxOption ? '' : null"
                          />
                          <span class="radio-label">Manage outbound access by security group rules</span>
                        </label>
                        @if (!shouldShowBasicSandboxOption) {
                          <div class="sandbox-unavailable-note">
                            <svg lucideInfo></svg>
                            <p>
                              Security group-based sandboxing is only available for recovery points that contain a
                              security group.
                            </p>
                          </div>
                        }
                      </div>
                    }
                    @if (!effectiveAdvancedNetworkSandbox && !isAzureApplication) {
                      <div class="nested-content">
                        <ng-container *ngTemplateOutlet="cidrBlocksTemplate"></ng-container>
                        <div class="nested-checkbox">
                          <label for="sandbox-allow-s3">
                            <input
                              id="sandbox-allow-s3"
                              class="arpio-checkbox"
                              type="checkbox"
                              formControlName="allowS3Checked"
                            />
                            Allow outbound access to S3 IPs
                          </label>
                        </div>
                        <div class="nested-checkbox half-margin-top">
                          <label>
                            <input
                              id="sandbox-allow-dynamo"
                              class="arpio-checkbox"
                              type="checkbox"
                              formControlName="allowDynamoChecked"
                            />
                            Allow outbound access to DynamoDB IPs
                          </label>
                        </div>
                      </div>
                    }
                  </div>
                }
                <ng-template #cidrBlocksTemplate>
                  <h5>Allow outbound access to these CIDR blocks</h5>
                  <div class="sandbox-scroll-list">
                    <string-list-control
                      class="spacer"
                      [validationFunction]="isValidCidr"
                      [normalizeFunction]="normalizeCidr"
                      [placeholderText]="whitelistPlaceholderText"
                      [frozen]="sandboxFrozen"
                      (validated)="whitelistValidated($event)"
                      formControlName="sandboxWhitelist"
                    ></string-list-control>
                  </div>
                </ng-template>
                @if (showAdvancedOptions) {
                  <div class="inline-disclosure margin-top" [class.is-open]="advancedOpen">
                    <button
                      type="button"
                      class="inline-disclosure-toggle"
                      (click)="advancedOpen = !advancedOpen"
                      [attr.aria-expanded]="advancedOpen"
                    >
                      <svg lucideChevronDown class="inline-disclosure-chevron"></svg>
                      <span>Advanced Options</span>
                    </button>
                    @if (advancedOpen) {
                      <div class="inline-disclosure-body">
                        <div class="flex-center-row nested-content">
                          <div class="nested-checkbox">
                            <label>
                              <input
                                class="arpio-checkbox slight-margin-top"
                                type="checkbox"
                                formControlName="forceNewTargetPlans"
                              />
                              Replan placement of recovery subnets
                            </label>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }
              </form>
              @if (this.sandboxFrozen) {
                <div class="notice-info margin-top">
                  <svg lucideInfo></svg>
                  <p>
                    Network sandbox settings cannot be changed because another {{ uiService.productName }} App is
                    already in RECOVERY TEST.
                  </p>
                </div>
              }
              <div class="modal-step-continue-row">
                <button
                  type="button"
                  class="arpio-secondary-button"
                  data-cy="step-continue-button"
                  (click)="continueToStep('review')"
                >
                  Continue
                </button>
              </div>
            </div>
          }
        </div>
      }
      <div id="modal-step-review" class="modal-step modal-step--collapsible" [class.is-open]="isStepOpen('review')">
        <div
          class="modal-step-label"
          role="button"
          tabindex="0"
          [attr.aria-expanded]="isStepOpen('review')"
          aria-controls="modal-step-review-body"
          (click)="toggleStep('review')"
          (keydown)="onStepHeaderKeydown($event, 'review')"
        >
          <span class="modal-step-number"></span>
          <h3 class="modal-step-title">Review</h3>
          <svg lucideChevronDown class="modal-step-chevron"></svg>
        </div>
        @if (isStepOpen('review')) {
          <div id="modal-step-review-body" class="modal-step-body">
            <p class="modal-body-description">
              Confirm the settings below, then choose <strong>Test Recovery</strong> to launch.
            </p>
            @if (appsMissingRecoveryPoint.length > 0) {
              <div class="notice-warning review-warning">
                <svg lucideCircleAlert></svg>
                <p>
                  <strong>{{ appsMissingRecoveryPoint.length }}</strong> selected
                  {{ appsMissingRecoveryPoint.length === 1 ? 'application has' : 'applications have' }} no recovery
                  point at this time. Pick a more recent point or deselect them to continue.
                </p>
              </div>
            }
            <h4 class="review-heading">Applications</h4>
            @if (selectedApplications.length > 0) {
              <ul class="review-list">
                @for (app of selectedApplications; track app) {
                  <ng-container *ngLet="computeApplicationRecoveryPoint(app) as appRp">
                    <li class="review-row" [class.has-error]="!appRp">
                      @if (appRp) {
                        <svg lucideCircleCheck class="review-row-icon"></svg>
                      } @else {
                        <svg lucideCircleAlert class="review-row-icon"></svg>
                      }
                      <span class="app-name">{{ app.name }}</span>
                      @if (appRp) {
                        @if (showLock(appRp)) {
                          <svg lucideLock class="lock-icon"></svg>
                        }
                        <span class="recovery-timestamp-text">{{ recoversToTimeString(app, appRp) }}</span>
                      } @else {
                        <span class="recovery-timestamp-error">No matching recovery point</span>
                      }
                    </li>
                  </ng-container>
                }
              </ul>
            } @else {
              <div class="app-selection-empty">
                <svg lucideInfo></svg>
                <span>No applications selected.</span>
              </div>
            }
            @if (accountService.networkSandboxEnabled) {
              <h4 class="review-heading">Network sandbox</h4>
              <p class="review-line">
                <strong>{{ networkSandboxChecked && canSandboxRecoveryPoint ? 'On' : 'Off' }}</strong>
                @if (networkSandboxChecked && canSandboxRecoveryPoint && !isAzureApplication) {
                  <span class="review-line-detail">
                    — outbound managed by
                    {{ effectiveAdvancedNetworkSandbox ? 'network firewall' : 'security group rules' }}
                  </span>
                }
              </p>
              @if (networkSandboxChecked && canSandboxRecoveryPoint) {
                <!-- Domains exist in advanced (Network Firewall) mode for AWS, and
                    whenever advanced mode is active for Azure. -->
                @if (effectiveAdvancedNetworkSandbox) {
                  <div class="review-detail">
                    <span class="review-detail-label">Allowed domains</span>
                    @if (networkSandboxAllowedDomains?.length) {
                      @for (domain of networkSandboxAllowedDomains; track domain) {
                        <code class="review-chip">{{ domain }}</code>
                      }
                    } @else {
                      <span class="review-empty">None</span>
                    }
                  </div>
                }
                <!-- CIDR blocks: shown in advanced mode (any platform) and basic
                    mode for AWS. Azure has no basic mode. -->
                @if (effectiveAdvancedNetworkSandbox || !isAzureApplication) {
                  <div class="review-detail">
                    <span class="review-detail-label">Allowed CIDR blocks</span>
                    @if (networkSandboxWhitelist?.length) {
                      @for (cidr of networkSandboxWhitelist; track cidr) {
                        <code class="review-chip">{{ cidr }}</code>
                      }
                    } @else {
                      <span class="review-empty">None</span>
                    }
                  </div>
                }
                <!-- S3 / DynamoDB toggles only apply in basic (Security Group) mode for AWS. -->
                @if (!effectiveAdvancedNetworkSandbox) {
                  <div class="review-detail">
                    <span class="review-detail-label">Allowed AWS services</span>
                    @if (prettyAllowedAwsServices.length > 0) {
                      @for (svc of prettyAllowedAwsServices; track svc) {
                        <code class="review-chip">{{ svc }}</code>
                      }
                    } @else {
                      <span class="review-empty">None</span>
                    }
                  </div>
                }
              }
            }
            @if (showAdvancedOptions && forceNewTargetPlans) {
              <h4 class="review-heading">Advanced</h4>
              <p class="review-line">Replan placement of recovery subnets</p>
            }
          </div>
        }
      </div>
      @if (currentlySyncingMessage) {
        <div class="syncing-banner">
          <svg lucideLoaderCircle class="lucide-spin"></svg>
          <p>
            Arpio is applying your latest recovery point. Once it's done applying, you will be able to test your
            recovery.
          </p>
        </div>
      }
      @if (dialogError.error) {
        <arp-error-block
          class="dialog-error"
          [error]="dialogError.error"
          [dismissible]="true"
          (dismissed)="dialogError.clear()"
        ></arp-error-block>
      }

      <div class="modal-footer">
        @if (dialogError.error?.blockedReason) {
          <span class="modal-footer-hint">{{ dialogError.error.blockedReason }}</span>
        }
        <button class="arpio-secondary-button" (click)="onCancelClick()" [disabled]="launching">Cancel</button>
        <button
          busy-button
          [busy]="launching"
          id="test-button"
          class="arpio-button"
          (click)="onLaunchClick()"
          [disabled]="!canLaunchTest || !!dialogError.error?.blockedReason"
        >
          Test Recovery
        </button>
      </div>
    } @else {
      <div class="launching-view">
        <h3 class="launching-section-title">Test Details</h3>
        <h4 class="review-heading">Applications</h4>
        @if (launchingSnapshot?.apps?.length > 0) {
          <ul class="review-list">
            @for (app of launchingSnapshot.apps; track app) {
              <li class="review-row">
                <svg lucideCircleCheck class="review-row-icon"></svg>
                <span class="app-name">{{ app.name }}</span>
                @if (app.rpTimeString) {
                  @if (app.showLock) {
                    <svg lucideLock class="lock-icon"></svg>
                  }
                  <span class="recovery-timestamp-text">{{ app.rpTimeString }}</span>
                }
              </li>
            }
          </ul>
        }
        @if (launchingSnapshot?.showNetworkSandboxSection) {
          <h4 class="review-heading">Network sandbox</h4>
          <p class="review-line">
            <strong>{{ launchingSnapshot.networkSandboxOn ? 'On' : 'Off' }}</strong>
            @if (launchingSnapshot.networkSandboxOn && !launchingSnapshot.isAzure) {
              <span class="review-line-detail">
                — outbound managed by
                {{ launchingSnapshot.effectiveAdvancedNetworkSandbox ? 'network firewall' : 'security group rules' }}
              </span>
            }
          </p>
          @if (launchingSnapshot.networkSandboxOn) {
            @if (launchingSnapshot.effectiveAdvancedNetworkSandbox) {
              <div class="review-detail">
                <span class="review-detail-label">Allowed domains</span>
                @if (launchingSnapshot.networkSandboxAllowedDomains?.length) {
                  @for (domain of launchingSnapshot.networkSandboxAllowedDomains; track domain) {
                    <code class="review-chip">{{ domain }}</code>
                  }
                } @else {
                  <span class="review-empty">None</span>
                }
              </div>
            }
            @if (launchingSnapshot.effectiveAdvancedNetworkSandbox || !launchingSnapshot.isAzure) {
              <div class="review-detail">
                <span class="review-detail-label">Allowed CIDR blocks</span>
                @if (launchingSnapshot.networkSandboxWhitelist?.length) {
                  @for (cidr of launchingSnapshot.networkSandboxWhitelist; track cidr) {
                    <code class="review-chip">{{ cidr }}</code>
                  }
                } @else {
                  <span class="review-empty">None</span>
                }
              </div>
            }
            @if (!launchingSnapshot.effectiveAdvancedNetworkSandbox) {
              <div class="review-detail">
                <span class="review-detail-label">Allowed AWS services</span>
                @if (launchingSnapshot.prettyAllowedAwsServices.length > 0) {
                  @for (svc of launchingSnapshot.prettyAllowedAwsServices; track svc) {
                    <code class="review-chip">{{ svc }}</code>
                  }
                } @else {
                  <span class="review-empty">None</span>
                }
              </div>
            }
          }
        }
        @if (launchingSnapshot?.showAdvanced) {
          <h4 class="review-heading">Advanced</h4>
          <p class="review-line">Replan placement of recovery subnets</p>
        }
      </div>
    }
  </div>
}
